import type { ReactNode } from 'react';
import { Button, Modal } from '@safetyhub/ui';
import type { PresetUnresolvedRef } from '@safetyhub/report-composer';
import type { WidgetPasteAssetAxis, WidgetPasteReport } from './nodeClipboard';

/** 미해소 자산 축별 안내 — "왜 못 옮겼는지"(대상 환경에 없는 자산)를 한 줄로 설명한다. */
const ASSET_AXIS_TEXT: Record<WidgetPasteAssetAxis, { label: string; why: string }> = {
  widgetKey: {
    label: '등록형 위젯',
    why: '이 환경의 코드에 등록되지 않은 위젯입니다. 배포 버전을 맞추면 해소됩니다.',
  },
  presetRef: {
    label: '프리셋 출처',
    why: '이 환경에 없는 프리셋입니다. 위젯 내용은 복사본이라 그대로 동작하고, 출처·버전 표시만 끊깁니다.',
  },
  catalogKey: {
    label: '시스템 조회',
    why: '이 환경에 없는 전역 시스템 조회 키입니다. 값이 채워지지 않으므로 속성에서 다른 키로 바꾸거나 조회를 먼저 동기화하세요.',
  },
  approvalMeta: {
    label: '결재 설정',
    why: '대상 양식에 이미 결재 설정이 있어 적용하지 않았습니다(부분 혼합 방지). 필요하면 결재 설정에서 직접 지정하세요.',
  },
};

/** 묶음 밖 위젯 참조 축 라벨 — 삽입 후 저작자가 대상을 직접 지정해야 하는 항목. */
const REF_AXIS_LABEL: Record<PresetUnresolvedRef['axis'], string> = {
  visibleWhen: '표시 조건',
  enabledWhen: '활성 조건',
  labelSuffixFrom: '라벨 병기',
  'writes.target': '전파 대상',
  'actionItem.opensWidgetId': '드롭다운 액션 대상',
  'commit.to': '모달 확인 착지',
  'compute.input': '계산 셀 입력',
};

type WidgetPasteReportModalProps = {
  /** 리포트가 있으면 열림. 닫으면 소비처가 null로 되돌린다. */
  report: WidgetPasteReport | null;
  onClose: () => void;
};

/** 키 목록 한 줄 — 빈 목록이면 렌더하지 않는다. */
function KeyLine({ label, keys }: { label: string; keys: string[] }) {
  if (keys.length === 0) return null;
  return (
    <p className="leading-relaxed">
      <span className="text-gray-500">{label}</span>{' '}
      <span className="break-all text-gray-700">{keys.join(', ')}</span>
    </p>
  );
}

/**
 * 키 나열 접기 — **기본 닫힘**. 이식됨/재사용은 건수만 알면 충분한 정보라, 키를 펼쳐 두면 정작 봐야 할
 * 미해소가 묻힌다(2026-07-22 사용자 지적 "내용이 너무 복잡한데?"). 필요한 사람만 열어 본다.
 * 네이티브 `<details>`는 대화상자가 아니라 모달 base 규약(local/no-raw-modal) 대상이 아니다.
 */
function KeyDetails({ children, count }: { children: ReactNode; count: number }) {
  if (count === 0) return null;
  return (
    <details className="text-xs">
      <summary className="cursor-pointer text-gray-500 hover:text-gray-700">
        키 보기 ({count})
      </summary>
      <div className="mt-1 space-y-1">{children}</div>
    </details>
  );
}

/**
 * 위젯 붙여넣기 이식 리포트 — **이식됨 / 기존 재사용 / 미해소** 3분류를 보여주는 정보 모달.
 *
 * 붙여넣기는 이 모달이 뜨는 시점에 **이미 적용된 뒤**라 액션은 확인 1개뿐이다(되돌리려면 Ctrl+Z).
 * 표시 여부는 소비처가 `shouldShowWidgetPasteReport`(순수 함수)로 판정한다 — 이 컴포넌트는 조건을
 * 소유하지 않는다. 분류 산출도 전부 `buildWidgetPasteReport` 몫이고 여기서는 문구만 입힌다.
 */
export function WidgetPasteReportModal({ report, onClose }: WidgetPasteReportModalProps) {
  const hasUnresolved =
    (report?.unresolved.refs.length ?? 0) > 0 || (report?.unresolved.assets.length ?? 0) > 0;
  const hasReused =
    (report?.reused.nodeKeys.length ?? 0) > 0 || (report?.reused.dataKeys.length ?? 0) > 0;

  return (
    <Modal
      open={report !== null}
      onClose={onClose}
      title="붙여넣기 결과"
      size="lg"
      footer={
        <Button variant="primary" autoFocus onClick={onClose}>
          확인
        </Button>
      }
    >
      <div className="space-y-4 text-sm text-gray-700">
        {/* 이식됨 — 이번 붙여넣기로 대상 양식에 새로 생긴 것. 개수 요약이 앞, 키 나열은 접기. */}
        <section className="space-y-1">
          <h3 className="text-sm font-semibold text-gray-800">이식됨</h3>
          <p className="leading-relaxed">
            {[
              `위젯 ${report?.ported.widgetCount ?? 0}개`,
              ...((report?.ported.nodeCount ?? 0) > 0 ? [`노드 ${report?.ported.nodeCount}개`] : []),
              ...((report?.ported.dataKeys.length ?? 0) > 0
                ? [`데이터 ${report?.ported.dataKeys.length}건`]
                : []),
            ].join(' · ')}
          </p>
          {/* 노드가 실제로 추가된 경우에만 **위치**를 알린다 — 작성 탭엔 노드 선택 컨텍스트가 없어
              본문 말미에 append되므로, 보고서 레이아웃상 원하는 자리에 두려면 양식 탭에서 옮겨야 한다.
              "함께 추가되었습니다"까지만 말하고 어디에 추가됐는지를 안 알려주면, 보고서 탭에서 표가
              끝에 붙은 것을 보고 고장으로 오인한다(2026-07-22 사용자 제보). */}
          {(report?.ported.nodeCount ?? 0) > 0 && (
            <p className="leading-relaxed text-gray-500">
              노드는 <strong className="font-medium text-gray-700">양식 본문 맨 끝</strong>에
              추가됩니다. 보고서에서의 위치는 양식 탭에서 옮겨 주세요.
            </p>
          )}
          {/* 작성 폼만 옮긴다는 범위 고지 — Ctrl+A 전체선택 후 "보고서까지 통째로 온다"는 기대와
              어긋나는 지점이라, 노드가 0개일 때(=재사용만 일어나 아무것도 안 온 것처럼 보일 때)도 필요하다. */}
          <p className="leading-relaxed text-gray-500">
            옮겨지는 것은 <strong className="font-medium text-gray-700">작성 폼(위젯)</strong>입니다 —
            보고서 본문 레이아웃 전체를 옮기려면 양식 탭에서 노드를 복사하세요.
          </p>
          <KeyDetails
            count={(report?.ported.nodeKeys.length ?? 0) + (report?.ported.dataKeys.length ?? 0)}
          >
            <KeyLine label="새 바인딩:" keys={report?.ported.nodeKeys ?? []} />
            <KeyLine label="새 데이터 소스:" keys={report?.ported.dataKeys ?? []} />
          </KeyDetails>
        </section>

        {/* 기존 재사용 — payload에 있었으나 대상에 이미 있어 추가하지 않은 것(기존 우선). */}
        {hasReused && (
          <section className="space-y-1">
            <h3 className="text-sm font-semibold text-gray-800">기존 재사용</h3>
            <p className="leading-relaxed">
              {[
                ...((report?.reused.nodeKeys.length ?? 0) > 0
                  ? [`바인딩 ${report?.reused.nodeKeys.length}건`]
                  : []),
                ...((report?.reused.dataKeys.length ?? 0) > 0
                  ? [`데이터 ${report?.reused.dataKeys.length}건`]
                  : []),
              ].join(' · ')}
              <span className="text-gray-500"> — 대상에 이미 있어 그대로 연결(덮어쓰지 않음)</span>
            </p>
            <KeyDetails
              count={(report?.reused.nodeKeys.length ?? 0) + (report?.reused.dataKeys.length ?? 0)}
            >
              <KeyLine label="바인딩:" keys={report?.reused.nodeKeys ?? []} />
              <KeyLine label="데이터 소스:" keys={report?.reused.dataKeys ?? []} />
            </KeyDetails>
          </section>
        )}

        {/* 미해소 — 자동으로 연결하지 못한 것. 사용자가 손봐야 하는 항목이라 근거까지 적는다. */}
        {hasUnresolved && (
          <section className="space-y-2">
            <h3 className="text-sm font-semibold text-amber-800">미해소 — 확인이 필요합니다</h3>
            {(report?.unresolved.assets.length ?? 0) > 0 && (
              <div className="space-y-1">
                <p className="text-xs leading-relaxed text-gray-500">
                  복사는 <b>양식이 자기 안에 가진 것</b>만 옮깁니다. 아래는 이 환경의 자산이라 함께
                  옮길 수 없었습니다.
                </p>
                <ul className="max-h-48 space-y-1.5 overflow-y-auto rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
                  {report?.unresolved.assets.map((a, i) => (
                    <li key={`${a.axis}-${a.value}-${i}`} className="leading-snug">
                      <span className="font-medium">{ASSET_AXIS_TEXT[a.axis].label}</span>
                      {' → '}
                      <code className="rounded bg-white px-1">{a.value}</code>
                      <span className="block text-amber-700">{ASSET_AXIS_TEXT[a.axis].why}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {(report?.unresolved.refs.length ?? 0) > 0 && (
              <div className="space-y-1">
                <p className="text-xs leading-relaxed text-gray-500">
                  아래 참조는 복사 묶음 밖을 가리켜 자동으로 연결하지 못했습니다. 붙여넣은 위젯의
                  속성에서 대상을 지정하세요.
                </p>
                <ul className="max-h-48 space-y-1 overflow-y-auto rounded border border-gray-200 bg-gray-50 p-2 text-xs text-gray-600">
                  {report?.unresolved.refs.map((r, i) => (
                    <li key={`${r.widgetId}-${r.axis}-${i}`} className="leading-snug">
                      <span className="font-medium text-gray-700">
                        {REF_AXIS_LABEL[r.axis] ?? r.axis}
                      </span>
                      {' → '}
                      <code className="rounded bg-white px-1 text-gray-500">{r.value}</code>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}
      </div>
    </Modal>
  );
}
